import React from "react";
import { Trash2, X } from "lucide-react";

import ButtonV2 from "../../../../components/ui/button/v2";
import FormField, { useTouched } from "./FormField";
import MultiSelectSummaryDropdown from "../../../../components/ui/selectdropdown/v2/MultiSelectSummaryDropdown";
import { OptionNameValuePair } from "../../../../lib/interfaces/types";
import { AreaOfFraud } from "../../../../api/cases";
import { RELATED_AS_LABELS, RelatedAs } from "../../../../api/stakeholder_relationships";
import { CYBER_PARTY_ROLES, OPERATIONAL_PARTY_ROLES } from "../../../../api/accounts";
import { RelationConnectorSvg } from "./PartyRelationRow";


export const getRelevantRoles = (areaOfFraud: AreaOfFraud) => {
  return areaOfFraud === AreaOfFraud.OPERATIONAL
      ? OPERATIONAL_PARTY_ROLES
      : areaOfFraud === AreaOfFraud.CYBER
      ? CYBER_PARTY_ROLES
      : [];
}

export interface AccountRelationshipGroup {
  id: string;
  relatedAs?: RelatedAs;
  accountNumbers: string[];
}

export interface AccountRelationGroupRowProps {
  group: AccountRelationshipGroup;
  allGroups: AccountRelationshipGroup[];
  areaOfFraud: AreaOfFraud;
  accountOptions: OptionNameValuePair<string>[];
  onChange: (next: AccountRelationshipGroup) => void;
  onDelete: () => void;
  showErrors: boolean;
  relationError?: string;
  accountNumbersError?: string;
  isFirst: boolean;
  isLast: boolean;
  parentHasError: boolean;
}

export const AccountPartyRelationGroupRow: React.FC<AccountRelationGroupRowProps> = ({
  group,
  allGroups,
  areaOfFraud,
  accountOptions,
  onChange,
  onDelete,
  showErrors,
  relationError,
  accountNumbersError,
  isFirst,
  isLast,
  parentHasError,
}) => {
  const { isTouched, markTouched } = useTouched();
  const show = (key: string) => showErrors || isTouched(key);
  const currentRelatedAs = group.relatedAs;

  const isPrimaryAccountHolderRow = currentRelatedAs === RelatedAs.PRIMARY_ACCOUNT_HOLDER;

  const usedRelatedAs = new Set(allGroups.map((r) => r.relatedAs).filter((relatedAs) => relatedAs !== currentRelatedAs));

  const relevantRoles = getRelevantRoles(areaOfFraud);

  const filteredRoles = relevantRoles.filter((role) => {
    if(usedRelatedAs.has(role)) {
      return false;
    } else if(!isPrimaryAccountHolderRow && role === RelatedAs.PRIMARY_ACCOUNT_HOLDER) {
      return false
    }
    return true;
  })

  const partyTypeOptions = filteredRoles.map((role) => ({
    name: RELATED_AS_LABELS[role] || role,
    value: role,
  }));

  const finalAccountError = showErrors && accountNumbersError ? accountNumbersError : undefined;

  const onPartyTypeChange = (relatedAs: RelatedAs) => {
    onChange({...group, relatedAs: relatedAs})
  }

  const onAccountsChange = (accountNumbers: string[]) => {
    onChange({...group, accountNumbers: accountNumbers})
  }

  return (
    <div className={`relative w-full ${isPrimaryAccountHolderRow ? "group" : ""}`}>
      {!isLast && <div className="absolute left-[-20px] top-0 bottom-[-12px] w-[1px] bg-[#E5E5E5] pointer-events-none" />}
      <RelationConnectorSvg isFirst={isFirst} hasParentError={isFirst && parentHasError} />

      <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-start">
        <FormField
          type="select"
          label="Party Type"
          required
          disabled={isPrimaryAccountHolderRow}
          placeholder="Select party type"
          value={currentRelatedAs || ""}
          options={partyTypeOptions}
          hideLabel={!isFirst}
          error={show(`type:${group.id}`) ? relationError : undefined}
          onChange={(v) => onPartyTypeChange(v as RelatedAs)}
          onBlur={() => markTouched(`type:${group.id}`)}
        />

        <div className="flex flex-col gap-1 min-w-0">
          {isFirst && <label className="text-xs font-medium text-gray-700">Accounts</label>}
          <div
            className={`flex items-center border rounded-md bg-white min-h-9 px-2.5 ${
              isPrimaryAccountHolderRow ? "cursor-not-allowed opacity-90" : ""
            } ${finalAccountError ? "border-red-500" : "border-[#E5E5E5]"}`}
          >
            <MultiSelectSummaryDropdown
              name="accounts"
              placeholder="Select accounts"
              value={group.accountNumbers}
              options={accountOptions}
              disabled={isPrimaryAccountHolderRow}
              hideChevronWhenSelected={false}
              onChange={(vals) => onAccountsChange(vals)}
              renderCustomView={() => {
                const selected = accountOptions.filter((o) => group.accountNumbers.includes(o.value));
                if (selected.length === 0) {
                  return <span className="text-sm text-[#737373]">Select accounts</span>;
                }
                return (
                  <div className="flex flex-wrap gap-1">
                    {selected.map((o) => (
                      <span
                        key={o.value}
                        className="inline-flex items-center gap-1 rounded-md bg-[#EBEEFE] px-2 py-0.5 text-xs font-medium text-[#304ADD]"
                      >
                        {o.name}
                        {!isPrimaryAccountHolderRow && (
                          <button
                            type="button"
                            className="hover:text-red-500"
                            onClick={(e) => {
                              e.stopPropagation();
                              onAccountsChange(selected.filter((option) => option.value !== o.value).map(option => option.value));
                            }}
                          >
                            <X width={12} height={12} />
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                );
              }}
            />
          </div>
          {finalAccountError && <span className="text-xs text-red-500">{finalAccountError}</span>}
        </div>

        <div className={`flex items-center ${isFirst ? "pt-5" : ""}`}>
          <ButtonV2
            variant="outline"
            size="sm"
            disabled={isPrimaryAccountHolderRow || allGroups.length <= 1}
            onClick={onDelete}
            title={allGroups.length <= 1 ? "At least one party type is required" : "Remove party type"}
            className="h-9 px-2 rounded-md bg-white border border-gray-300 text-red-500 hover:bg-red-500 hover:text-white hover:border-red-500 disabled:opacity-40"
            prefix={<Trash2 width="16" height="16" />}
          />
        </div>
      </div>

      {isPrimaryAccountHolderRow && (
        <div className="pointer-events-none absolute left-0 top-full z-50 mt-1 hidden w-max max-w-[400px] rounded-[6px] bg-[#1F2937] px-3 py-2 text-xs text-white shadow-lg group-hover:block">
          Party is added as Primary Account Holder in Accounts
        </div>
      )}
    </div>
  );
};

export default AccountPartyRelationGroupRow;